// ============================================================================
// CANCUN100 — PDFCambio.jsx (C1b — Cupón de Cambio comparativo, paleta morada)
// ============================================================================
// data (además de servicios/cliente/representante de buildDataComision):
//   folio (folio_cambio), motivo, folio_original,
//   comision_original, comision_simulada, diferencia,
//   comparativo: [{ nombre, sku, antesPax, antesNeto, despuesPax, despuesNeto,
//                   estado:'agregado'|'eliminado'|'modificado'|'igual', delta,
//                   hayAntes, hayDespues, esCombo }],
//   desglose3: [{ label, antes, despues, esComision? }]
// ============================================================================
import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import './pdfFonts';
import { fmtMXN } from './pdfStyles';
import { LOGO_BASE64 } from './logoBase64';

const P = {
  morado: '#8b5cf6', moradoClaro: '#a78bfa', banda: '#f3e8ff',
  texto: '#1a1a2e', muted: '#6b7280', border: '#e5e7eb',
  verde: '#059669', rojo: '#dc2626', naranja: '#ff8c42', gris: '#9ca3af',
};
const COL_ESTADO = { agregado: P.verde, eliminado: P.rojo, modificado: P.naranja, igual: P.gris };
const signo = (n) => `${n >= 0 ? '+' : '−'}$${fmtMXN(Math.abs(n || 0))}`;

const s = StyleSheet.create({
  page: { paddingTop: 36, paddingBottom: 36, paddingHorizontal: 38, fontFamily: 'DM Sans', color: P.texto, fontSize: 10 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  logo: { width: 150, height: 34, objectFit: 'contain' },
  folioBox: { backgroundColor: P.banda, borderRadius: 6, paddingVertical: 10, paddingHorizontal: 14, minWidth: 150, alignItems: 'flex-start', border: `1 solid ${P.moradoClaro}` },
  folioLabel: { fontSize: 7, color: P.morado, letterSpacing: 1.2, fontFamily: 'DM Sans', fontWeight: 700 },
  folioNum: { fontSize: 15, color: P.morado, fontFamily: 'Space Grotesk', fontWeight: 700, marginTop: 2 },
  banda: { backgroundColor: P.morado, color: '#fff', textAlign: 'center', paddingVertical: 6, borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: 1, marginBottom: 12 },
  sectionTitle: { fontSize: 9, color: P.morado, fontWeight: 700, letterSpacing: 1, marginTop: 12, marginBottom: 6, textTransform: 'uppercase' },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3, borderBottom: `0.5 solid ${P.border}` },
  infoLabel: { color: P.muted },
  th: { flexDirection: 'row', borderBottom: `1 solid ${P.morado}`, paddingBottom: 4, marginBottom: 2 },
  thCell: { fontSize: 7, color: P.morado, fontWeight: 700, letterSpacing: 0.5 },
  tr: { flexDirection: 'row', paddingVertical: 3, borderBottom: `0.5 solid ${P.border}`, alignItems: 'flex-start' },
  colServ: { flex: 3 }, colAntes: { flex: 2 }, colDespues: { flex: 2 }, colDelta: { flex: 1.4, textAlign: 'right' },
  nombre: { fontSize: 9, color: P.texto },
  sub: { fontSize: 6, color: P.muted },
  cell: { fontSize: 8, color: P.texto },
  cellMuted: { fontSize: 8, color: P.muted },
  tag: { fontSize: 6, fontWeight: 700, textTransform: 'uppercase' },
  // Desglose 3 columnas
  dc: { marginTop: 16, border: `1.5 solid ${P.morado}`, borderRadius: 8, padding: 10 },
  dcTitle: { fontSize: 10, color: P.morado, fontWeight: 700, marginBottom: 6, letterSpacing: 1 },
  dcHead: { flexDirection: 'row', borderBottom: `1 solid ${P.morado}`, paddingBottom: 3, marginBottom: 3 },
  dcHeadCell: { fontSize: 7, color: P.morado, fontWeight: 700 },
  dcRow: { flexDirection: 'row', paddingVertical: 3, alignItems: 'center' },
  dcLabel: { flex: 2, fontSize: 9 },
  dcNum: { flex: 1.3, fontSize: 9, textAlign: 'right', fontFamily: 'Space Grotesk' },
  dcComRow: { flexDirection: 'row', paddingVertical: 6, marginTop: 4, borderTop: `1 solid ${P.morado}`, alignItems: 'center' },
  dcComLabel: { flex: 2, fontSize: 11, fontWeight: 700 },
  dcComNum: { flex: 1.3, fontSize: 11, textAlign: 'right', fontFamily: 'Space Grotesk', fontWeight: 700 },
});

const PDFCambio = ({ data }) => {
  const comparativo = data?.comparativo || [];
  const desglose3 = data?.desglose3 || [];
  const dif = Number(data?.diferencia) || 0;

  return (
    <Document>
      <Page size="LETTER" style={s.page}>
        <View style={s.header}>
          <Image src={LOGO_BASE64} style={s.logo} />
          <View style={s.folioBox}>
            <Text style={s.folioLabel}>CUPÓN DE CAMBIO</Text>
            <Text style={s.folioNum}>{data?.folio || '—'}</Text>
            <Text style={{ fontSize: 8, color: P.muted, marginTop: 2 }}>{data?.fecha_creacion || ''}</Text>
          </View>
        </View>

        <Text style={s.banda}>SIMULACIÓN — SUJETO A AUTORIZACIÓN</Text>

        <View style={s.infoRow}><Text style={s.infoLabel}>Cliente</Text><Text>{data?.cliente?.nombre || '—'}</Text></View>
        <View style={s.infoRow}><Text style={s.infoLabel}>Representante</Text><Text>{data?.representante?.nombre || '—'}</Text></View>
        <View style={s.infoRow}><Text style={s.infoLabel}>Folio original</Text><Text>{data?.folio_original || '—'}</Text></View>
        <View style={s.infoRow}><Text style={s.infoLabel}>Motivo del cambio</Text><Text>{data?.motivo || '—'}</Text></View>

        <Text style={s.sectionTitle}>Comparativo de servicios (antes → después)</Text>
        <View style={s.th}>
          <Text style={[s.thCell, s.colServ]}>SERVICIO</Text>
          <Text style={[s.thCell, s.colAntes]}>ANTES (pax · neto)</Text>
          <Text style={[s.thCell, s.colDespues]}>DESPUÉS (pax · neto)</Text>
          <Text style={[s.thCell, s.colDelta]}>Δ NETO</Text>
        </View>
        {comparativo.map((r, i) => {
          const col = COL_ESTADO[r.estado] || P.gris;
          return (
            <View key={i} style={s.tr} wrap={false}>
              <View style={s.colServ}>
                <Text style={s.nombre}>{r.nombre}{r.esCombo ? '' : ''}</Text>
                <Text style={[s.tag, { color: col }]}>{r.sku}{r.estado !== 'igual' ? ` · ${r.estado}` : ' · ='}</Text>
              </View>
              <View style={s.colAntes}>
                {r.hayAntes
                  ? <Text style={[s.cell, r.estado === 'eliminado' ? { color: P.rojo, textDecoration: 'line-through' } : {}]}>{r.antesPax} · ${fmtMXN(r.antesNeto)}</Text>
                  : <Text style={s.cellMuted}>—</Text>}
              </View>
              <View style={s.colDespues}>
                {r.hayDespues
                  ? <Text style={[s.cell, r.estado === 'agregado' ? { color: P.verde } : r.estado === 'modificado' ? { color: P.naranja } : {}]}>{r.despuesPax} · ${fmtMXN(r.despuesNeto)}</Text>
                  : <Text style={{ fontSize: 8, color: P.rojo, fontWeight: 700 }}>eliminado</Text>}
              </View>
              <Text style={[s.cell, s.colDelta, { color: Math.abs(r.delta) < 0.01 ? P.gris : (r.delta >= 0 ? P.verde : P.rojo), fontFamily: 'Space Grotesk', fontWeight: 700 }]}>
                {Math.abs(r.delta) < 0.01 ? '=' : signo(r.delta)}
              </Text>
            </View>
          );
        })}

        <View style={s.dc}>
          <Text style={s.dcTitle}>DOBLE CONTABILIDAD</Text>
          <View style={s.dcHead}>
            <Text style={[s.dcHeadCell, { flex: 2 }]}></Text>
            <Text style={[s.dcHeadCell, { flex: 1.3, textAlign: 'right' }]}>ANTES</Text>
            <Text style={[s.dcHeadCell, { flex: 1.3, textAlign: 'right' }]}>DESPUÉS</Text>
            <Text style={[s.dcHeadCell, { flex: 1.3, textAlign: 'right' }]}>Δ</Text>
          </View>
          {desglose3.filter((d) => !d.esComision).map((d, i) => {
            const delta = Number(d.despues) - Number(d.antes);
            return (
              <View key={i} style={s.dcRow}>
                <Text style={s.dcLabel}>{d.label}</Text>
                <Text style={s.dcNum}>${fmtMXN(d.antes)}</Text>
                <Text style={s.dcNum}>${fmtMXN(d.despues)}</Text>
                <Text style={[s.dcNum, { color: Math.abs(delta) < 0.01 ? P.gris : (delta >= 0 ? P.verde : P.rojo) }]}>{Math.abs(delta) < 0.01 ? '=' : signo(delta)}</Text>
              </View>
            );
          })}
          {desglose3.filter((d) => d.esComision).map((d, i) => (
            <View key={i} style={s.dcComRow}>
              <Text style={[s.dcComLabel, { color: P.morado }]}>COMISIÓN</Text>
              <Text style={s.dcComNum}>${fmtMXN(d.antes)}</Text>
              <Text style={s.dcComNum}>${fmtMXN(d.despues)}</Text>
              <Text style={[s.dcComNum, { color: dif >= 0 ? P.verde : P.rojo }]}>{dif >= 0 ? '+' : '−'}${fmtMXN(Math.abs(dif))} {dif >= 0 ? 'A FAVOR' : 'EN CONTRA'}</Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default PDFCambio;
